import { FC, ReactElement, useState, useEffect } from 'react'
import styled from 'styled-components'
import ValueBox from 'components/ValueBox'
import Chart from 'components/Chart'

interface SummaryProps {
  colorMode: 'dark' | 'light'
  locale: 'de' | 'fr'
  hideCharts: boolean | undefined
}
// TODO
const isMobile = false
const firstOfMonth = (date: string | Date) => {
  let month = new Date(date).getMonth()
  let day = 2
  let year = new Date(date).getFullYear()
  return new Date(year, month, day)
}
const addDays = (days: number, date: Date) => {
  var newdate = new Date(date)
  newdate.setDate(newdate.getDate() + days)
  return newdate
}

const Summary: FC<SummaryProps> = ({
  colorMode,
  locale,
  hideCharts,
}): ReactElement => {
  const [data, setData] = useState<any>(null)
  const [chartData, setChartData] = useState<any>(null)
  const chartsOptions = {
    height: 150,
    xTicks: {
      text: [
        firstOfMonth('2020-03-10').toISOString().split('T')[0],
        firstOfMonth(new Date()).toISOString().split('T')[0],
      ],
      lines: [firstOfMonth(new Date()).toISOString().split('T')[0]],
    },
    locale: locale,
    colorMode: colorMode,
    type: 'multiline',
  }

  useEffect(() => {
    // Facts
    window
      .fetch(
        `https://interactif.tdg.ch/live-data-romandie/covid-daily/summary_data_ch_v4.json?nocache=${new Date().getTime()}`
      )
      .then((res) => {
        res.json().then((json) => {
          setData(json)
        })
      })
      .catch((e) => {
        console.warn(e.message)
      })

    // Charts
    if (!hideCharts) {
      window
        .fetch(
          `https://ta-interaktiv.storage.googleapis.com/corona-dashboard/dashboard-facts_2.json?nocache=${new Date().getTime()}`
        )
        .then((res) => {
          res.json().then((json) => {
            setChartData(json)
          })
        })
        .catch((e) => {
          console.warn(e.message)
        })
    }
  }, [])
  return data ? (
    <>
      <Title>
        {locale === 'fr'
          ? 'La situation en Suisse au ' + data.ofsp_report_date_str
          : 'Situation in der Schweiz am ' +
            new Date(data.ofsp_report_date).toLocaleDateString('de-CH', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
      </Title>
      <Lead>
        {locale === 'fr'
          ? 'Chaque mardi vers 15h30, l’Office fédéral de la santé publique (OFSP) publie un rapport sur la semaine écoulée.'
          : 'Daten stammen vom BAG nach Meldedatum. Das BAG publiziert jeweils am Dienstag um 15:30 Uhr neue Zahlen.'}
      </Lead>
      <Grid>
        <ValueBox
          label={locale === 'fr' ? 'Nouveaux cas' : 'Bestätigte Fälle'}
          value={data.cases_change.last_week}
          percentage={data.cases_change.percent_increase}
          colorMode={colorMode}
          color='#000'
        />
        <ValueBox
          label={locale === 'fr' ? 'Nouveaux décès*' : 'Todesfälle*'}
          value={data.deaths.last_week}
          percentage={data.deaths.percent_increase}
          colorMode={colorMode}
          color='#fff'
          bgcolor='#d3343f'
        />
        <ValueBox
          label={locale === 'fr' ? 'Personnes hospitalisées' : 'Spitalbelegung'}
          value={data.hosp_capacity.last_day}
          percentage={data.hosp_capacity.percent_increase}
          colorMode={colorMode}
          color='#000'
          bgcolor='#60b9b7'
        />
        <ValueBox
          label={locale === 'fr' ? 'Nouvelles admissions' : 'Spitaleintritte'}
          value={data.hosp_change.last_week}
          percentage={data.hosp_change.percent_increase}
          colorMode={colorMode}
          color='#fff'
          bgcolor='#e16d00'
        />
      </Grid>
      <Footnote>
        {locale === 'fr'
          ? '*Liechtenstein inclus. L’OFSP ne communique plus le détail du nombre de décès en Suisse uniquement.'
          : '*Einschliesslich Liechtenstein. Das BAG publiziert die Anzahl der Todesfälle für die Schweiz nur noch in dieser Form.'}
      </Footnote>

      {chartData && (
        <>
          <Chart
            title={
              locale === 'fr' ? 'Taux de positivité' : 'Testpositivitätsrate'
            }
            legend={
              locale === 'fr'
                ? 'Tests PCR et antigéniques'
                : 'PCR- und Antigen-Tests, wöchentlich'
            }
            data={chartData['caseTestRatio']}
            lineColor={colorMode === 'dark' ? '#5E67CC' : '#202346'}
            areaColor='none'
            xDomain={[
              new Date('2020-03-01'),
              new Date(addDays(isMobile ? 120 : 50, new Date())),
            ]}
            yDomain={[0, 70]}
            yTicks={{
              lines: [0, 35, 70],
              text: [35, 70],
              suffix: '%',
            }}
            {...chartsOptions}
          ></Chart>
          <Grid>
            <Chart
              title={locale === 'fr' ? 'Cas confirmés' : 'Bestätigte Fälle'}
              legend={
                locale === 'fr'
                  ? 'Moyenne mobile sur 7 jours'
                  : 'Durchschnitt der letzten 7 Tage'
              }
              data={chartData['allAve7Cases_']}
              lineColor='#e78d38'
              areaColor='#ffe462'
              xDomain={[
                new Date('2020-03-01'),
                new Date(addDays(isMobile ? 120 : 50, new Date())),
              ]}
              yDomain={[0, 50000]}
              yTicks={{
                lines: [0, 25000, 50000],
                text: [25000, 50000],
              }}
              {...chartsOptions}
            ></Chart>
            <Chart
              title={locale === 'fr' ? 'Décès' : 'Todesfälle'}
              legend={
                locale === 'fr'
                  ? 'Moyenne mobile sur 7 jours'
                  : 'Durchschnitt der letzten 7 Tage'
              }
              data={chartData['allAve7Death_']}
              lineColor='#d3343f'
              areaColor='#f4cccf'
              xDomain={[
                new Date('2020-01-01'),
                new Date(addDays(isMobile ? 120 : 50, new Date())),
              ]}
              yDomain={[0, 120]}
              yTicks={{
                lines: [0, 60, 120],
                text: [60, 120],
              }}
              {...chartsOptions}
            ></Chart>
            <ValueBox
              label={locale === 'fr' ? 'Total des cas*' : 'Gesamt Fälle*'}
              value={4401341}
              colorMode={colorMode}
              color='#000'
              prettify
              locale={locale}
            />
            <ValueBox
              label={locale === 'fr' ? 'Total des décès' : 'Gesamt Töte*'}
              value={14147}
              colorMode={colorMode}
              color='#fff'
              bgcolor='#d3343f'
              prettify
              locale={locale}
            />
          </Grid>
          <Footnote>
            {locale === 'fr'
              ? '*Une même personne peut figurer plusieurs fois dans le total des cas confirmés, si elle a été plusieurs fois infectée et testée positive.'
              : '*Eine Person kann mehrmals in der Gesamtzahl der bestätigten Fälle erscheinen, wenn sie mehrmals infiziert und positiv getestet wurde.'}
          </Footnote>
        </>
      )}
    </>
  ) : (
    <Placeholder>{locale === 'fr' ? 'Chargement…' : 'Laden…'}</Placeholder>
  )
}

const Title = styled.h4`
  margin-bottom: 0;
  font-famly: var(--font-noto);
`
const Lead = styled.div`
  font-size: 0.9em;
  font-family: var(--font-plex);
  margin-bottom: 1em;
`

const Grid = styled.div`
  align-items: flex-start;
  flex-wrap: wrap;
  display: flex;
  grid-gap: 20px;
  gap: 20px;
`

const Footnote = styled.p`
  font-family: var(--font-plex);
  font-size: 0.8em;
  line-height: 1.2;
`

const Placeholder = styled.div``

export default Summary
